if (!customElements.get('pod-inquiry')) {
  class PodInquiry extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      // Landing pages that reuse this dialog mark their root with data-pod-root.
      this.root = this.closest('[data-pod-root]') || this.closest('.pod-lp');
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.root || !this.dialog || !this.form) return;

      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.steps = [...this.querySelectorAll('[data-pod-step]')];
      this.products = [...this.querySelectorAll('[data-pod-product]')];
      this.storageKey = `tidal-pod:${location.pathname}:${this.form.id}`;
      this.currentStep = 0;
      // Validate visible steps ourselves so the browser never focuses a hidden field.
      this.form.noValidate = true;

      this.root.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-pod-open]');
        if (!opener || opener.getAttribute('aria-controls') !== this.dialog.id) return;
        event.preventDefault();
        const product = this.products.find((input) => input.dataset.podProduct === opener.dataset.podCategory);
        if (product) {
          product.checked = true;
          this.updateCategory();
          this.showStep(0, false);
        }
        this.open(opener);
      }, options);

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-pod-header-open]');
        if (!opener || document.querySelector('pod-inquiry') !== this) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.openFromHeader(opener);
      }, options);

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-pod-close]')) this.dialog.close();
        if (event.target.closest('[data-pod-next]')) this.next();
        if (event.target.closest('[data-pod-back]')) this.showStep(this.currentStep - 1);
      }, options);
      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[data-pod-product]')) this.updateCategory();
      }, options);
      this.form.addEventListener('submit', (event) => this.submit(event), options);
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropPressed = this.isBackdrop(event);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.backdropPressed && this.isBackdrop(event)) this.dialog.close();
        this.backdropPressed = false;
      }, options);
      this.dialog.addEventListener('close', () => this.afterClose(), options);
      window.addEventListener('pageshow', () => this.setSubmitting(false), options);

      const result = this.querySelector('[data-pod-result]');
      if (result?.dataset.podResult === 'success') this.clearSaved();
      if (result?.dataset.podResult === 'error') this.restoreSaved();
      if (!this.products.some((input) => input.checked)) {
        const defaultProduct = this.products.find((input) => input.dataset.podProduct === this.dataset.defaultCategory);
        if (defaultProduct) defaultProduct.checked = true;
      }
      this.updateCategory();
      if (this.steps.length) {
        const firstInvalidStep = this.steps.findIndex((step) => this.invalidField(step));
        const recoveryStep = firstInvalidStep < 0 ? this.steps.length - 1 : firstInvalidStep;
        this.showStep(result ? recoveryStep : 0, false);
      }
      if (result) this.open();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      if (this.dialog?.open) this.dialog.close();
      this.unlockScroll();
    }

    async openFromHeader(opener) {
      if (this.openingFromHeader || this.dialog.open) return;
      this.openingFromHeader = true;
      try {
        const drawer = opener.closest('menu-drawer');
        let returnFocus = opener;
        if (drawer?.hasAttribute('open') && typeof drawer.hide === 'function') {
          returnFocus = drawer.activeElement || document.querySelector('button[aria-controls="MenuDrawer"]');
          await drawer.hide();
          // The theme releases its drawer focus trap on the next task.
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
        if (this.isConnected) this.open(returnFocus);
      } finally {
        this.openingFromHeader = false;
      }
    }

    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener || null;
      this.dialog.showModal();
      this.scrollStyles = [document.documentElement, document.body].map((element) => {
        const saved = [element, element.style.getPropertyValue('overflow'), element.style.getPropertyPriority('overflow')];
        element.style.setProperty('overflow', 'hidden');
        return saved;
      });
      const focusTarget = this.querySelector('[data-pod-result]') || this.steps[this.currentStep]?.querySelector('[data-pod-heading]');
      focusTarget?.focus({ preventScroll: true });
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    afterClose() {
      this.unlockScroll();
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }

    unlockScroll() {
      this.scrollStyles?.forEach(([element, value, priority]) => {
        if (value) element.style.setProperty('overflow', value, priority);
        else element.style.removeProperty('overflow');
      });
      this.scrollStyles = null;
    }

    updateCategory() {
      const product = this.products.find((input) => input.checked);
      this.querySelectorAll('[data-pod-fields]').forEach((fieldset) => {
        const active = fieldset.dataset.podFields === product?.dataset.podProduct;
        fieldset.hidden = !active;
        fieldset.disabled = !active;
      });
      const intros = {
        tshirts: 'Let\'s plan your T-shirts: choose a style and tell us about your print.',
        apparel: 'Let\'s plan your apparel: choose your garments and share your preferences.',
        blankets: 'Let\'s plan your blankets: choose a style and share your size, material, and print preferences.',
        towels: 'Let\'s plan your towels: choose a style and share your size, material, and print preferences.',
      };
      const intro = this.querySelector('[data-pod-project-intro]');
      if (intro) intro.textContent = intros[product?.dataset.podProduct] || product?.dataset.podIntro || 'Share a few details about your project.';
      const summary = this.querySelector('[data-pod-summary]');
      if (summary) summary.textContent = product?.value || '';
    }

    showStep(index, focus = true) {
      if (!this.steps.length) return;
      this.currentStep = Math.max(0, Math.min(index, this.steps.length - 1));
      this.steps.forEach((step, i) => { step.hidden = i !== this.currentStep; });
      this.querySelectorAll('[data-pod-progress]').forEach((item, i) => {
        if (i === this.currentStep) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
        item.classList.toggle('is-complete', i < this.currentStep);
      });
      this.querySelector('[data-pod-back]').hidden = this.currentStep === 0;
      this.querySelector('[data-pod-next]').hidden = this.currentStep === this.steps.length - 1;
      this.querySelector('[data-pod-submit]').hidden = this.currentStep !== this.steps.length - 1;
      this.querySelector('[data-pod-announcement]').textContent = `Step ${this.currentStep + 1} of ${this.steps.length}`;
      if (focus) {
        this.dialog.scrollTop = 0;
        this.steps[this.currentStep].querySelector('[data-pod-heading]').focus({ preventScroll: true });
      }
    }

    invalidField(step) {
      return [...step.querySelectorAll('input, select, textarea')].find((field) => field.willValidate && !field.checkValidity());
    }

    next() {
      const invalid = this.invalidField(this.steps[this.currentStep]);
      if (invalid) return invalid.reportValidity();
      this.showStep(this.currentStep + 1);
    }

    submit(event) {
      if (this.currentStep < this.steps.length - 1) {
        event.preventDefault();
        this.next();
        return;
      }
      for (let index = 0; index < this.steps.length; index++) {
        const invalid = this.invalidField(this.steps[index]);
        if (invalid) {
          event.preventDefault();
          this.showStep(index);
          invalid.reportValidity();
          return;
        }
      }
      if (this.submitting) {
        event.preventDefault();
        return;
      }
      // Keep a short-lived, tab-local copy only when sending, for a Shopify error return.
      try {
        const fields = [...new FormData(this.form)].filter(([name]) => name.startsWith('contact['));
        sessionStorage.setItem(this.storageKey, JSON.stringify({ at: Date.now(), fields }));
      } catch (_) { /* Storage can be unavailable in private browsing. */ }
      // Leave Shopify's native submission and spam protection in control.
      // If Shopify cancels submission for a challenge, the button stays usable.
      queueMicrotask(() => { if (!event.defaultPrevented) this.setSubmitting(true); });
    }

    setSubmitting(busy) {
      this.submitting = busy;
      const button = this.querySelector('[data-pod-submit]');
      if (!button) return;
      button.dataset.label ||= button.textContent.trim();
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
      button.textContent = busy ? 'Sending...' : button.dataset.label;
    }

    restoreSaved() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey));
        if (!saved || Date.now() - saved.at > 30 * 60 * 1000) return this.clearSaved();
        for (const [name, value] of saved.fields) {
          [...this.form.elements].filter((field) => field.name === name).forEach((field) => {
            if (field.type === 'radio') field.checked = field.value === value;
            else field.value = value;
          });
        }
      } catch (_) { /* Native form values are still available without storage. */ }
    }

    clearSaved() {
      try { sessionStorage.removeItem(this.storageKey); } catch (_) { /* Optional storage. */ }
    }
  }
  customElements.define('pod-inquiry', PodInquiry);
}
